/**
 * Contract between the page designer shell (admin panel, parent window) and the canvas
 * frame (landing app, `/editor-frame/<slug>`, iframe). Cross-origin: every postMessage names the
 * peer's origin explicitly and every listener checks `event.origin`.
 */
import type { Block } from "./blocks";
import type { Page } from "./page";

export const EDITOR_FRAME_PATH = (slug: string) => `/editor-frame/${encodeURIComponent(slug)}`;

export type Device = "desktop" | "tablet" | "mobile";
export const DEVICE_WIDTH: Record<Device, number | "100%"> = {
  desktop: "100%",
  tablet: 834,
  mobile: 390,
};

export type EditorMode = "edit" | "preview";

/** Element/block kinds the Add panel can drop into the canvas. */
export const PALETTE_MIME = "application/x-aurumiq-block-type";

export type ShellToFrame =
  | {
      type: "aurumiq:editor:state";
      page: Page;
      selectedId: string | null;
      mode: EditorMode;
      device: Device;
    }
  | { type: "aurumiq:editor:scrollTo"; id: string }
  | { type: "aurumiq:editor:mode"; mode: EditorMode }
  /** Answer to `replaceImage`: the media the user picked. */
  | {
      type: "aurumiq:editor:media";
      requestId: string;
      media: { id: string; url: string; alt: string; width?: number; height?: number } | null;
    };

export type FrameToShell =
  | { type: "aurumiq:editor:ready" }
  | { type: "aurumiq:editor:select"; id: string | null }
  | { type: "aurumiq:editor:hover"; id: string | null }
  /** Inline edit committed. `path` is dot/bracket-free: "headline", "items.0.title", "elements.2.html". */
  | { type: "aurumiq:editor:setField"; id: string; path: string; value: unknown }
  /** Whole block replaced (free-layout drags/resizes commit the full block to keep it atomic). */
  | { type: "aurumiq:editor:setBlock"; id: string; block: Block }
  | { type: "aurumiq:editor:move"; id: string; toIndex: number }
  | { type: "aurumiq:editor:insert"; index: number; blockType: Block["type"] }
  | {
      type: "aurumiq:editor:action";
      id: string;
      action:
        | "duplicate"
        | "delete"
        | "toggle"
        | "up"
        | "down"
        | "settings"
        | "style"
        | "add-after";
    }
  | {
      type: "aurumiq:editor:replaceImage";
      requestId: string;
      id: string;
      path: string;
      current: string | null;
    }
  | { type: "aurumiq:editor:height"; height: number }
  | { type: "aurumiq:editor:shortcut"; key: "undo" | "redo" | "save" };

export function getPath(obj: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (acc, k) => (acc == null ? undefined : (acc as Record<string, unknown>)[k]),
      obj,
    );
}

/** Immutable set along a dot path; arrays are cloned as arrays. */
export function setPath<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split(".");
  const clone: Record<string, unknown> | unknown[] = Array.isArray(obj)
    ? [...(obj as unknown[])]
    : { ...(obj as Record<string, unknown>) };
  let cur: Record<string, unknown> | unknown[] = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i] as string;
    const next = (cur as Record<string, unknown>)[k];
    (cur as Record<string, unknown>)[k] = Array.isArray(next)
      ? [...next]
      : { ...((next as Record<string, unknown>) || {}) };
    cur = (cur as Record<string, unknown>)[k] as Record<string, unknown>;
  }
  (cur as Record<string, unknown>)[keys[keys.length - 1] as string] = value;
  return clone as T;
}

/** Every editable string leaf of a block (skips id/type/style/anchor), for inline text editing. */
export function editableStrings(
  block: Record<string, unknown>,
  prefix = "",
): { path: string; value: string }[] {
  const out: { path: string; value: string }[] = [];
  const skip = new Set(["id", "type", "style", "anchor", "hidden", "label"]);
  for (const [k, v] of Object.entries(block)) {
    if (!prefix && skip.has(k)) continue;
    const p = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out.push({ path: p, value: v });
    else if (Array.isArray(v))
      v.forEach((item, i) => {
        if (typeof item === "string") out.push({ path: `${p}.${i}`, value: item });
        else if (item && typeof item === "object")
          out.push(...editableStrings(item as Record<string, unknown>, `${p}.${i}`));
      });
    else if (v && typeof v === "object")
      out.push(...editableStrings(v as Record<string, unknown>, p));
  }
  return out;
}

export const newBlockId = (prefix = "b"): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
